import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { getBitacorasPadrones } from 'api/tramites/bitacora';
import { getPredios, getPredio } from 'api/catastro/predio';
import { getUsuarios } from 'api/usuarios/usuarios';
import address from 'utils/address';
import { discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import {
  normalizeSearchParams,
  getModalTitle,
  extractDescripcionFromRecord,
  formatFecha,
  GLOBAL_ACCIONES,
  computeRangoInfo,
} from 'utils/bitacoraHelpers';

export const ACCIONES_DESCUENTOS = [
  { id: 'APLICAR_DESCUENTO', descripcion: 'Descuentos aplicados' },
  { id: 'CANCELAR_CARGO', descripcion: 'Cargos cancelados' },
  { id: 'CARGO_PRESCRIPTO', descripcion: 'Cargos prescriptos' },
  { id: 'CARGO_EXENTO', descripcion: 'Cargos exentos' },
];

const PAGES_PER_CHUNK = 20;
const CHUNK_SIZE = 1000; // PAGES_PER_CHUNK × 50 registros por página API

// Clave de agrupación: mismo predio + acción + usuario + tipo de descuento
// (sin bucket de tiempo — el rango de fechas se calcula sobre los registros del grupo)
const makeGroupKey = (record) => {
  const clave = record.clave || '-';
  const accion = record.accion_inf || '-';
  const usuario = String(record.usuario_creo || '-');
  const tipo = String(
    record.data_nueva?.tipo_de_descuento
    ?? record.data_anterior?.tipo_de_descuento
    ?? '-',
  );
  return `${clave}|${accion}|${usuario}|${tipo}`;
};

/*
Centraliza en una sola fila todos los registros del mismo tipo de descuento/cancelación
aplicados al mismo predio por el mismo usuario dentro del rango buscado.
El _count indica cuántos cargos fueron afectados; _fechaInicio/_fechaFin muestran el rango.
*/
const groupBitacoraRecords = (records) => {
  if (!records.length) return [];
  const groups = new Map();
  records.forEach((record) => {
    const key = makeGroupKey(record);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(record);
  });
  return Array.from(groups.values()).map((grp) => {
    const sorted = [...grp].sort(
      (a, b) => new Date(a.fecha_hora || 0) - new Date(b.fecha_hora || 0),
    );
    const rep = sorted[0];
    const esAnual = sorted.some((r) => r.data_salida?.es_descuento_anual === true);
    const esPeriodo = sorted.some((r) => r.data_salida?.aplica_por_periodo === true);
    return {
      ...rep,
      _count: sorted.length,
      _records: sorted,
      _fechaInicio: sorted[0].fecha_hora,
      _fechaFin: sorted[sorted.length - 1].fecha_hora,
      _esAnual: esAnual,
      _esPeriodo: esPeriodo,
    };
  });
};

const fmtN = (val) => {
  const n = Number(val);
  return Number.isFinite(n) && n > 0
    ? n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
};

const extractPredioInfo = (predio) => {
  if (!predio) return null;
  let propietario = '—';
  if (predio.nombres_de_participes?.length) {
    propietario = predio.nombres_de_participes.join(', ');
  } else {
    const obj = predio.ciudadano || predio.contribuyente;
    if (obj && typeof obj === 'object') {
      propietario = obj.nombre_completo
        || ['nombre', 'apellido_paterno', 'apellido_materno']
          .map((f) => obj[f] || '').join(' ').trim() || '—';
    }
  }
  const supT = fmtN(predio.superficie_total_del_terreno);
  const supC = fmtN(predio.superficie_de_la_construccion);
  const valC = fmtN(predio.valor_de_la_construccion);
  const valF = fmtN(predio.valor_catastral);
  let direccion = null;
  if (predio.direccion) {
    direccion = typeof predio.direccion === 'string'
      ? predio.direccion
      : address(predio.direccion);
  }
  return {
    propietario,
    cuentaPredial: predio.cuenta_unica_de_predial || null,
    cuentaCatastral: predio.clave_catastral_municipal || null,
    supTerreno: supT ? `${supT} m²` : null,
    supConstruccion: supC ? `${supC} m²` : null,
    valorConstruccion: valC ? `$${valC}` : null,
    valorFiscal: valF ? `$${valF}` : null,
    tipoPredio: predio.tipo_de_predio?.descripcion || null,
    direccion,
  };
};

/*
Hook exclusivo para la bitácora de descuentos / cargos.
Centraliza los registros por aplicación (mismo predio, acción, usuario, tipo y minuto)
y obtiene todas las páginas de la bitácora via polling para un agrupamiento completo.
*/
// eslint-disable-next-line import/prefer-default-export
export const useBitacoraDescuentos = () => {
  const [loading, setLoading] = useState(false);
  const [cuentasLoading, setCuentasLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [rawResults, setRawResults] = useState([]);
  const [rawCount, setRawCount] = useState(0);
  const [dataParams, setDataParams] = useState({});
  const [selected, setSelected] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [cuentasPrediales, setCuentasPrediales] = useState({});
  const [prediosData, setPrediosData] = useState({});
  const [currentChunk, setCurrentChunk] = useState(1);
  const chunksCacheRef = useRef({});

  const groupedResults = useMemo(
    () => groupBitacoraRecords(rawResults),
    [rawResults],
  );

  const data = useMemo(() => ({
    results: groupedResults,
    count: groupedResults.length,
    rawCount,
    params: dataParams,
  }), [groupedResults, rawCount, dataParams]);

  const totalChunks = useMemo(
    () => (rawCount > 0 ? Math.ceil(rawCount / CHUNK_SIZE) : 1),
    [rawCount],
  );

  const getNombreUsuario = useCallback((id, row) => {
    if (row?.accion_inf === 'LOGIN') return row.data_nueva?.email || '';
    if (!id || !usuarios.length) return '';
    const user = usuarios.find((u) => u.id === id);
    return user?.nombre_completo || '';
  }, [usuarios]);

  const fetchCuentasPrediales = useCallback(async (results, preResolvedMap = {}) => {
    if (!results.length) return;
    setCuentasLoading(true);
    const map = {};
    const predioMap = {};
    try {
      const conClave = results.filter((r) => r.clave && r.clave !== '-');
      const sinClave = results.filter((r) => !r.clave || r.clave === '-');

      conClave.forEach((r) => {
        if (preResolvedMap[r.clave]) map[r.id] = preResolvedMap[r.clave];
      });

      const clavesUnicas = [...new Set(conClave.map((r) => r.clave))]
        .filter((c) => !preResolvedMap[c]);
      await Promise.all(
        clavesUnicas.map(async (clave) => {
          try {
            const res = await getPredios({ clave, page_size: 1 });
            const predio = res?.results?.[0];
            const cuenta = predio?.clave_catastral_municipal ?? null;
            if (cuenta) {
              conClave.filter((r) => r.clave === clave).forEach((r) => { map[r.id] = cuenta; });
            }
            if (predio) {
              predioMap[clave] = extractPredioInfo(predio);
            }
          } catch { /* continúa */ }
        }),
      );
      setPrediosData((prev) => ({ ...prev, ...predioMap }));

      const getObjectId = (row) => {
        const raw = row.data_salida?.object_id ?? row.data_anterior?.object_id;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const esPredio = (row) => {
        const ct = row.data_anterior?.content_type;
        return typeof ct === 'string' && ct.toLowerCase().includes('predio');
      };
      const sinClaveValidos = sinClave.filter((r) => getObjectId(r) && esPredio(r));
      const objectIdsUnicos = [...new Set(sinClaveValidos.map(getObjectId))];

      await Promise.all(
        objectIdsUnicos.map(async (objectId) => {
          try {
            const predio = await getPredio(objectId, false);
            const cuenta = predio?.clave_catastral_municipal ?? null;
            if (cuenta) {
              sinClaveValidos
                .filter((r) => getObjectId(r) === objectId)
                .forEach((r) => { map[r.id] = cuenta; });
            }
          } catch { /* continúa */ }
        }),
      );

      setCuentasPrediales((prev) => ({ ...prev, ...map }));
    } finally {
      setCuentasLoading(false);
    }
  }, []);

  /*
  Obtiene las PAGES_PER_CHUNK páginas del chunk solicitado.
  Estrategia: primera petición para conocer el total, luego el resto en paralelo
  */
  const fetchChunk = useCallback(async (params, chunkNumber) => {
    const startPage = (chunkNumber - 1) * PAGES_PER_CHUNK + 1;

    const firstResponse = await getBitacorasPadrones({ ...params, page: startPage });
    const count = firstResponse?.count ?? 0;
    const firstResults = firstResponse?.results || [];

    if (!firstResponse?.next) {
      return { results: firstResults, count };
    }

    const totalApiPages = Math.ceil(count / firstResults.length);
    const endPage = Math.min(startPage + PAGES_PER_CHUNK - 1, totalApiPages);
    const remainingPages = Array.from(
      { length: endPage - startPage },
      (_, i) => startPage + i + 1,
    );

    const remainingResponses = await Promise.all(
      remainingPages.map((page) => getBitacorasPadrones({ ...params, page })),
    );

    const allResults = [
      ...firstResults,
      ...remainingResponses.flatMap((r) => r?.results || []),
    ];

    return { results: allResults, count };
  }, []);

  const fetchUsuarios = useCallback(async () => {
    setUsuariosLoading(true);
    try {
      const usuariosData = await getUsuarios();
      setUsuarios(usuariosData);
    } catch (error) {
      onError(error);
    } finally {
      setUsuariosLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const wipe = useCallback(() => {
    setSearchParams({});
    setSelectedRowKeys([]);
    setSelected(null);
    setRawResults([]);
    setRawCount(0);
    setDataParams({});
    setCuentasPrediales({});
    setPrediosData({});
    setCurrentChunk(1);
    chunksCacheRef.current = {};
    return {
      usuario_creo: undefined,
      clave: undefined,
      cuenta_unica: undefined,
      fecha_inicio: undefined,
      fecha_fin: undefined,
      accion_inf: undefined,
    };
  }, []);

  const handleOnRowClick = useCallback((record) => {
    setSelectedRowKeys([record.id]);
    setSelected(record);
  }, []);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    type: 'radio',
  }), [selectedRowKeys]);

  const handleSearch = useCallback(async (formValues) => {
    try {
      setLoading(true);
      const resolvedValues = { ...formValues };
      const preResolvedMap = {};

      if (formValues.cuenta_unica) {
        const predioRes = await getPredios({
          clave_catastral_municipal: formValues.cuenta_unica,
          page_size: 1,
        });
        const predio = predioRes?.results?.[0];
        if (!predio?.clave) {
          throw new Error('No se encontró ningún predio con esa cuenta única');
        }
        resolvedValues.clave = predio.clave;
        preResolvedMap[predio.clave] = predio.clave_catastral_municipal || formValues.cuenta_unica;
        setPrediosData((prev) => ({ ...prev, [predio.clave]: extractPredioInfo(predio) }));
      }
      delete resolvedValues.cuenta_unica;

      const paramsSearch = normalizeSearchParams({ ...resolvedValues, id_padron: undefined });
      const keys = Object.keys(discartNullValues(paramsSearch));
      if (!keys.length) {
        setRawResults([]);
        throw new Error('Ingrese al menos un campo para realizar la búsqueda');
      }

      chunksCacheRef.current = {};
      const { results: chunkResults, count } = await fetchChunk(paramsSearch, 1);
      chunksCacheRef.current[1] = chunkResults;
      setCurrentChunk(1);
      setRawResults(chunkResults);
      setRawCount(count);
      setDataParams(paramsSearch);
      setSearchParams(paramsSearch);
      await fetchCuentasPrediales(chunkResults, preResolvedMap);
      return { results: chunkResults, count };
    } catch (error) {
      onError(error, () => setLoading(false));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCuentasPrediales, fetchChunk]);

  const getModalTitleFromSelected = useCallback(() => getModalTitle(selected), [selected]);

  const ALL_ACCIONES = useMemo(() => GLOBAL_ACCIONES, []);

  const getColumns = useCallback(() => [
    {
      titleText: 'Cuenta Predial',
      dataIndex: 'cuenta_predial',
      key: 'cuenta_predial',
      width: 100,
      render: (_, row) => cuentasPrediales[row.id] || '—',
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      render: (_, row) => extractDescripcionFromRecord(row),
    },
    {
      titleText: 'Porcentaje',
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      width: 80,
      render: (_, row) => {
        const variables = row.data_nueva?.variables || [];
        const v = variables.find((x) => x.nombre_de_variable === 'PORCENTAJE_DESC');
        return v != null ? `${v.valor}%` : '—';
      },
    },
    {
      titleText: 'Rango de períodos',
      dataIndex: 'rango_periodos',
      key: 'rango_periodos',
      width: 240,
      render: (_, row) => computeRangoInfo(row._records || []) || '—',
    },
    {
      titleText: 'Usuario creó',
      dataIndex: 'usuario_creo',
      key: 'usuario_creo',
      width: 100,
      render: (id, row) => getNombreUsuario(id, row),
    },
    {
      titleText: 'Fecha',
      dataIndex: '_fechaInicio',
      key: '_fechaInicio',
      width: 120,
      render: (_, row) => formatFecha(row._fechaInicio || row.ultima_fecha_de_modificacion),
    },
    {
      titleText: 'Acción realizada',
      dataIndex: 'accion_inf',
      key: 'accion_inf',
      width: 150,
      render: (text) => {
        if (!text) return '';
        const match = ALL_ACCIONES.find((a) => a.id === text);
        return match ? match.descripcion.toUpperCase() : text.replace(/_/g, ' ');
      },
    },
  ], [getNombreUsuario, cuentasPrediales, ALL_ACCIONES]);

  const handlePageChange = useCallback(async (newChunk) => {
    if (chunksCacheRef.current[newChunk]) {
      setRawResults(chunksCacheRef.current[newChunk]);
      setCurrentChunk(newChunk);
      return;
    }
    setLoading(true);
    try {
      const { results: chunkResults } = await fetchChunk(searchParams, newChunk);
      chunksCacheRef.current[newChunk] = chunkResults;
      setRawResults(chunkResults);
      setCurrentChunk(newChunk);
      await fetchCuentasPrediales(chunkResults);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [searchParams, fetchChunk, fetchCuentasPrediales]);

  return {
    loading: loading || usuariosLoading || cuentasLoading,
    cuentasLoading,
    usuarios,
    data,
    selected,
    selectedRowKeys,
    searchParams,
    currentPage: currentChunk,
    totalChunks,
    getColumns,
    getModalTitleFromSelected,
    fetchData: handleSearch,
    wipe,
    handleOnRowClick,
    rowSelection,
    handleSearch,
    handlePageChange,
    setSelected,
    setSelectedRowKeys,
    setSearchParams,
    bitacoraLoading: loading,
    usuariosLoading,
    prediosData,
  };
};
